import { useState, useEffect } from 'react';

interface Document {
  id: number;
  fileName: string;
  originalFileName: string;
  fileSize: number;
  mimeType: string;
  category: string;
  description?: string;
  tags: string[];
  ownerId: string;
  ownerType: string;
  status: string;
  uploadedAt: string;
  updatedAt: string;
}

interface UseDocumentsOptions {
  category?: string;
  status?: string;
  autoRefresh?: boolean;
  ownerType?: "agency" | "client" | "employee" | "prospect";
  clientId?: number;
  employeeId?: number;
  prospectId?: number;
  folderId?: number;
  unfiled?: boolean;
}

export function useDocuments(options: UseDocumentsOptions = {}) {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (options.category) params.append("category", options.category);
      if (options.status) params.append("status", options.status);
      if (options.ownerType) params.append("ownerType", options.ownerType);
      if (options.clientId !== undefined)
        params.append("clientId", String(options.clientId));
      if (options.employeeId !== undefined)
        params.append("employeeId", String(options.employeeId));
      if (options.prospectId !== undefined)
        params.append("prospectId", String(options.prospectId));
      if (options.folderId !== undefined)
        params.append("folderId", String(options.folderId));
      if (options.unfiled) params.append("unfiled", "true");

      const queryString = params.toString();
      const url = `/api/documents${queryString ? `?${queryString}` : ""}`;

      const response = await fetch(url, { credentials: "include" });

      if (!response.ok) {
        throw new Error("Failed to fetch documents");
      }

      const data = await response.json();
      setDocuments(data.documents || []);
    } catch (err: any) {
      console.error("useDocuments: Error fetching documents:", err);
      setError(err.message || "Failed to fetch documents");
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshDocuments = () => {
    fetchDocuments();
  };

  const uploadDocument = async (
    file: File,
    metadata: {
      category: string;
      description?: string;
      tags?: string[];
      folderId?: number | null;
    }
  ) => {
    const formData = new FormData();
    // Server expects field name 'document'
    formData.append("document", file);
    formData.append("category", metadata.category);

    if (metadata.description) {
      formData.append("description", metadata.description);
    }
    if (metadata.tags && metadata.tags.length > 0) {
      formData.append("tags", JSON.stringify(metadata.tags));
    }
    if (metadata.folderId !== undefined && metadata.folderId !== null) {
      formData.append("folderId", String(metadata.folderId));
    }

    const response = await fetch("/api/documents/upload", {
      method: "POST",
      body: formData,
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Upload failed");
    }

    const result = await response.json();

    // Refresh documents list after successful upload
    if (options.autoRefresh !== false) {
      await fetchDocuments();
    }

    return result;
  };

  const deleteDocument = async (documentId: number) => {
    const response = await fetch(`/api/documents/${documentId}`, {
      method: "DELETE",
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Delete failed");
    }

    // Refresh documents list after successful delete
    if (options.autoRefresh !== false) {
      await fetchDocuments();
    }

    return true;
  };

  const updateDocument = async (
    documentId: number,
    updates: {
      category?: string;
      description?: string;
      tags?: string[];
      status?: string;
      folderId?: number | null;
    }
  ) => {
    const response = await fetch(`/api/documents/${documentId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updates),
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Update failed");
    }

    const result = await response.json();

    // Refresh documents list after successful update
    if (options.autoRefresh !== false) {
      await fetchDocuments();
    }

    return result;
  };

  const getDocument = async (documentId: number) => {
    const response = await fetch(`/api/documents/${documentId}`, {
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to get document");
    }

    return response.json();
  };

  const getDownloadUrl = async (documentId: number) => {
    const response = await fetch(`/api/documents/${documentId}/download`, {
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to get download URL");
    }

    const data = await response.json();
    return data.downloadUrl;
  };

  // Categories for dropdown/filter options
  const categories = [
    { value: "license", label: "License" },
    { value: "insurance", label: "Insurance" },
    { value: "policy", label: "Policy" },
    { value: "compliance", label: "Compliance" },
    { value: "form", label: "Form" },
    { value: "other", label: "Other" },
  ];

  useEffect(() => {
    console.log("useDocuments: useEffect triggered with options:", options);
    fetchDocuments();
  }, [
    options.category,
    options.status,
    options.ownerType,
    options.clientId,
    options.employeeId,
    options.folderId,
    options.unfiled,
    options.prospectId,
  ]);

  return {
    documents,
    loading,
    error,
    refreshDocuments,
    uploadDocument,
    deleteDocument,
    updateDocument,
    getDocument,
    getDownloadUrl,
    categories,
  };
}
